import { jsx as _jsx } from "react/jsx-runtime";
// Общая авторизация приложения (bootstrap ОДИН раз на корне) — объединяет логику
// App.tsx и canvas/useAuthBootstrap, чтобы переключение Чат↔Студия не пере-логинилось.
//   web-mode (/web): web-token → authed; иначе гость (anon) — «лендинг=app»,
//     весь шелл виден, гейт входа стоит на действии (как на /app);
//   иначе: session-token → web-token → TG initData (poll) → 'anon' (демо-режим).
import { createContext, useContext, useEffect, useState } from 'react';
import { api, getAuthToken, setAuthToken } from '../api/client';
import { getTelegramInitData, getTelegramWebApp } from '../hooks/useTelegram';
import i18n from '../i18n';
const AuthCtx = createContext(null);
export function useAuth() {
    const v = useContext(AuthCtx);
    if (!v)
        throw new Error('useAuth must be used within <AuthProvider>');
    return v;
}
export function AuthProvider({ children }) {
    const [status, setStatus] = useState('pending');
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const refreshMe = async () => {
        try {
            setUser(await api.getMe());
        }
        catch { /* ignore */ }
    };
    useEffect(() => {
        let cancelled = false;
        const done = (s, u) => {
            if (cancelled)
                return;
            if (u !== undefined) {
                setUser(u);
                i18n.changeLanguage(u.language);
            }
            setStatus(s);
        };
        const isWebMode = window.location.pathname.startsWith('/web');
        (async () => {
            // web-mode: вход только по web-token, иначе на /login
            if (isWebMode) {
                let webToken = null;
                try {
                    webToken = localStorage.getItem('pm:jwt:web');
                }
                catch { /* ignore */ }
                if (!webToken) {
                    done('anon');
                    return;
                }
                setAuthToken(webToken);
                try {
                    return done('authed', await api.getMe());
                }
                catch {
                    try {
                        localStorage.removeItem('pm:jwt:web');
                    }
                    catch { /* ignore */ }
                    setAuthToken('');
                    done('anon');
                    return;
                }
            }
            // TG / generic
            try {
                const tg = getTelegramWebApp();
                tg?.ready();
                tg?.expand();
                // Отключаем вертикальные свайпы (drag-to-close) — на iOS из-за них
                // весь Mini App «прыгает»/тянется при скролле контента. Bot API 7.7+.
                tg?.disableVerticalSwipes?.();
            }
            catch (e) {
                setError('tg.init: ' + e.message);
            }
            // 1) существующий session-token
            if (getAuthToken()) {
                try {
                    return done('authed', await api.getMe());
                }
                catch {
                    try {
                        sessionStorage.removeItem('pm:jwt');
                    }
                    catch { /* ignore */ }
                    setAuthToken('');
                }
            }
            // 2) web-token из localStorage
            let webToken = null;
            try {
                webToken = localStorage.getItem('pm:jwt:web');
            }
            catch { /* ignore */ }
            if (webToken) {
                setAuthToken(webToken);
                try {
                    return done('authed', await api.getMe());
                }
                catch {
                    setAuthToken('');
                }
            }
            // 3) TG initData (поллинг до 1.5с — SDK поднимает асинхронно на iOS).
            // Вне Telegram (обычный веб-визит на корень) поллить нечего — сразу anon,
            // иначе публичная главная висела бы 1.5с на пустом ожидании.
            let inTelegram = false;
            try {
                inTelegram = !!window.Telegram?.WebApp;
            }
            catch { /* ignore */ }
            let initData = '';
            if (inTelegram) {
                for (let i = 0; i < 15 && !cancelled; i++) {
                    initData = getTelegramInitData();
                    if (initData)
                        break;
                    await new Promise((r) => setTimeout(r, 100));
                }
            }
            if (initData) {
                try {
                    const { token, user: u } = await api.authViaTelegram(initData);
                    setAuthToken(token);
                    return done('authed', u);
                }
                catch (e) {
                    setError('authViaTelegram: ' + e.message);
                }
            }
            done('anon');
        })();
        return () => { cancelled = true; };
    }, []);
    return (_jsx(AuthCtx.Provider, { value: { status, user, error, setUser, refreshMe }, children: children }));
}
